import { useEffect, useMemo, useState } from "react";
import { useMutation, useQueryClient } from "@tanstack/react-query";
import { Play, RefreshCw } from "lucide-react";

import { GlobalAppMenu } from "@/components/nav/GlobalAppMenu";
import { apiClient } from "@/services/api/client";
import {
  useOpsDatasyncBootstrapQuery,
  useOpsDatasyncRunLogsQuery,
  useOpsDatasyncRunsQuery,
  useOpsDatasyncRunStepsQuery,
} from "@/features/workbench/queries";
import type { OpsDatasyncRun, OpsDatasyncSchedule } from "@/services/api/types";

function toRunId(raw: string | number) {
  const parsed = Number(raw);
  return Number.isFinite(parsed) ? parsed : 0;
}

function formatTimestamp(raw: string | null | undefined) {
  const value = String(raw || "").trim();
  if (!value) {
    return "--";
  }
  return value;
}

function statusTone(status: string | null | undefined) {
  const value = String(status || "").toUpperCase();
  if (["DONE", "SUCCESS", "COMPLETED", "ACTIVE"].includes(value)) {
    return "text-emerald-300";
  }
  if (["FAILED", "ERROR", "CANCELLED", "DELETED"].includes(value)) {
    return "text-rose-300";
  }
  if (["RUNNING", "QUEUED", "PAUSED"].includes(value)) {
    return "text-amber-300";
  }
  return "text-slate-300";
}

interface ScheduleFormState {
  editingScheduleId: number | null;
  name: string;
  taskType: string;
  scopeType: string;
  scheduleKind: "daily" | "weekly" | "monthly";
  time: string;
  weekdays: string;
  monthdays: string;
}

function toCsvNumbers(raw: string, min: number, max: number) {
  const values = String(raw || "")
    .split(/[,\s]+/)
    .map((item) => Number(item.trim()))
    .filter((item) => Number.isInteger(item) && item >= min && item <= max);
  return Array.from(new Set(values));
}

function scheduleFrequencyLabel(schedule: OpsDatasyncSchedule) {
  const cfg = schedule.schedule_config_json || {};
  const time = String(cfg.time || "--");
  if (schedule.schedule_kind === "weekly") {
    const weekdays = Array.isArray(cfg.weekdays) ? cfg.weekdays.join(",") : "--";
    return `weekly @ ${time} / ${weekdays}`;
  }
  if (schedule.schedule_kind === "monthly") {
    const monthdays = Array.isArray(cfg.monthdays) ? cfg.monthdays.join(",") : "--";
    return `monthly @ ${time} / ${monthdays}`;
  }
  return `daily @ ${time}`;
}

export function OpsWorkbenchPage() {
  const queryClient = useQueryClient();
  const bootstrapQuery = useOpsDatasyncBootstrapQuery();
  const runsQuery = useOpsDatasyncRunsQuery(80);

  const runs = useMemo(() => {
    if (runsQuery.data && runsQuery.data.length) {
      return runsQuery.data;
    }
    return bootstrapQuery.data?.runs || [];
  }, [bootstrapQuery.data?.runs, runsQuery.data]);
  const schedules = bootstrapQuery.data?.schedules || [];
  const canManage = Boolean(bootstrapQuery.data?.permissions?.can_manage);
  const taskTypes = bootstrapQuery.data?.task_types || [];
  const scheduleKinds = bootstrapQuery.data?.schedule_kinds || [];

  const [selectedRunId, setSelectedRunId] = useState<number | null>(null);
  const [scheduleForm, setScheduleForm] = useState<ScheduleFormState>({
    editingScheduleId: null,
    name: "",
    taskType: "",
    scopeType: "all",
    scheduleKind: "daily",
    time: "15:05",
    weekdays: "1,2,3,4,5",
    monthdays: "1",
  });

  useEffect(() => {
    if (!taskTypes.length) {
      return;
    }
    setScheduleForm((prev) => {
      if (prev.taskType) {
        return prev;
      }
      return { ...prev, taskType: String(taskTypes[0].key || "daily_sync") };
    });
  }, [taskTypes]);

  useEffect(() => {
    if (!runs.length) {
      setSelectedRunId(null);
      return;
    }
    const stillValid = selectedRunId != null && runs.some((item) => toRunId(item.id) === selectedRunId);
    if (!stillValid) {
      setSelectedRunId(toRunId(runs[0].id));
    }
  }, [runs, selectedRunId]);

  const selectedRun = useMemo<OpsDatasyncRun | null>(
    () => runs.find((item) => toRunId(item.id) === selectedRunId) || null,
    [runs, selectedRunId],
  );
  const logsQuery = useOpsDatasyncRunLogsQuery(selectedRunId, { cursor: 0, limit: 250 });
  const runStepsQuery = useOpsDatasyncRunStepsQuery(selectedRunId);

  const resetScheduleForm = () =>
    setScheduleForm((prev) => ({
      editingScheduleId: null,
      name: "",
      taskType: prev.taskType || String(taskTypes[0]?.key || "daily_sync"),
      scopeType: "all",
      scheduleKind: "daily",
      time: "15:05",
      weekdays: "1,2,3,4,5",
      monthdays: "1",
    }));

  const loadScheduleToForm = (schedule: OpsDatasyncSchedule) => {
    const cfg = schedule.schedule_config_json || {};
    setScheduleForm({
      editingScheduleId: Number(schedule.id || 0) || null,
      name: String(schedule.name || ""),
      taskType: String(schedule.task_type || taskTypes[0]?.key || "daily_sync"),
      scopeType: String(schedule.scope_type || "all"),
      scheduleKind: (String(schedule.schedule_kind || "daily").toLowerCase() as ScheduleFormState["scheduleKind"]) || "daily",
      time: String(cfg.time || "15:05"),
      weekdays: Array.isArray(cfg.weekdays) ? cfg.weekdays.join(",") : "1,2,3,4,5",
      monthdays: Array.isArray(cfg.monthdays) ? cfg.monthdays.join(",") : "1",
    });
  };

  const triggerDailySyncMutation = useMutation({
    mutationFn: () =>
      apiClient.createOpsDatasyncRun({
        task_type: "daily_sync",
        scope_type: "all",
        scope_json: {},
      }),
    onSuccess: async (payload) => {
      const runId = Number(payload?.run_id || 0);
      await Promise.all([
        queryClient.invalidateQueries({ queryKey: ["ops", "datasync", "bootstrap"] }),
        queryClient.invalidateQueries({ queryKey: ["ops", "datasync", "runs"] }),
      ]);
      if (runId > 0) {
        setSelectedRunId(runId);
      }
    },
  });

  const cancelRunMutation = useMutation({
    mutationFn: (runId: number) => apiClient.cancelOpsDatasyncRun(runId),
    onSuccess: async () => {
      await Promise.all([
        queryClient.invalidateQueries({ queryKey: ["ops", "datasync", "bootstrap"] }),
        queryClient.invalidateQueries({ queryKey: ["ops", "datasync", "runs"] }),
      ]);
      void logsQuery.refetch();
    },
  });

  const retryRunMutation = useMutation({
    mutationFn: (runId: number) => apiClient.retryOpsDatasyncRun(runId),
    onSuccess: async (payload) => {
      const runId = Number(payload?.run_id || 0);
      await Promise.all([
        queryClient.invalidateQueries({ queryKey: ["ops", "datasync", "bootstrap"] }),
        queryClient.invalidateQueries({ queryKey: ["ops", "datasync", "runs"] }),
      ]);
      if (runId > 0) {
        setSelectedRunId(runId);
      }
    },
  });

  const toggleScheduleMutation = useMutation({
    mutationFn: (payload: { id: number; action: "pause" | "resume" }) =>
      payload.action === "pause"
        ? apiClient.pauseOpsDatasyncSchedule(payload.id)
        : apiClient.resumeOpsDatasyncSchedule(payload.id),
    onSuccess: async () => {
      await Promise.all([
        queryClient.invalidateQueries({ queryKey: ["ops", "datasync", "bootstrap"] }),
        queryClient.invalidateQueries({ queryKey: ["ops", "datasync", "runs"] }),
      ]);
    },
  });

  const deleteScheduleMutation = useMutation({
    mutationFn: (scheduleId: number) => apiClient.deleteOpsDatasyncSchedule(scheduleId),
    onSuccess: async () => {
      await Promise.all([
        queryClient.invalidateQueries({ queryKey: ["ops", "datasync", "bootstrap"] }),
        queryClient.invalidateQueries({ queryKey: ["ops", "datasync", "runs"] }),
      ]);
    },
  });

  const saveScheduleMutation = useMutation({
    mutationFn: async (formState: ScheduleFormState) => {
      const payload: Record<string, unknown> = {
        name: String(formState.name || "").trim(),
        task_type: String(formState.taskType || "daily_sync"),
        scope_type: String(formState.scopeType || "all"),
        scope_json: {},
        schedule_kind: formState.scheduleKind,
        schedule_config_json: {
          time: String(formState.time || "15:05"),
        },
      };
      if (!payload.name) {
        throw new Error("schedule_name_required");
      }
      const cfg = payload.schedule_config_json as Record<string, unknown>;
      if (formState.scheduleKind === "weekly") {
        cfg.weekdays = toCsvNumbers(formState.weekdays, 1, 7);
      } else if (formState.scheduleKind === "monthly") {
        cfg.monthdays = toCsvNumbers(formState.monthdays, 1, 31);
      }
      if (formState.editingScheduleId && formState.editingScheduleId > 0) {
        return apiClient.updateOpsDatasyncSchedule(formState.editingScheduleId, payload);
      }
      return apiClient.createOpsDatasyncSchedule(payload);
    },
    onSuccess: async () => {
      await Promise.all([
        queryClient.invalidateQueries({ queryKey: ["ops", "datasync", "bootstrap"] }),
        queryClient.invalidateQueries({ queryKey: ["ops", "datasync", "runs"] }),
      ]);
      resetScheduleForm();
    },
  });

  const pageError =
    (bootstrapQuery.error as Error | null)?.message ||
    (runsQuery.error as Error | null)?.message ||
    (logsQuery.error as Error | null)?.message ||
    (runStepsQuery.error as Error | null)?.message ||
    (triggerDailySyncMutation.error as Error | null)?.message ||
    (cancelRunMutation.error as Error | null)?.message ||
    (retryRunMutation.error as Error | null)?.message ||
    (toggleScheduleMutation.error as Error | null)?.message ||
    (deleteScheduleMutation.error as Error | null)?.message ||
    (saveScheduleMutation.error as Error | null)?.message ||
    "";

  return (
    <main className="flex h-dvh min-h-0 flex-col overflow-hidden bg-[var(--bg)] text-[var(--text)]">
      <header className="border-b border-[var(--border)] bg-[#101318]">
        <div className="flex h-12 items-center gap-3 px-3">
          <GlobalAppMenu className="shrink-0" />
          <div className="min-w-0">
            <h1 className="text-sm font-semibold text-slate-100">运维看板</h1>
            <p className="text-[11px] text-[var(--muted)]">DataSync 运行 / 调度 / 日志</p>
          </div>
          <div className="ml-auto flex items-center gap-2">
            <button
              className="flex h-8 items-center gap-1 rounded-md border border-[var(--border)] bg-[#0c0f14] px-2 text-xs text-[var(--muted)] hover:text-slate-100"
              onClick={() => {
                void bootstrapQuery.refetch();
                void runsQuery.refetch();
                void logsQuery.refetch();
                void runStepsQuery.refetch();
              }}
            >
              <RefreshCw className="size-3.5" />
              刷新
            </button>
            <button
              className="flex h-8 items-center gap-1 rounded-md border border-[var(--border)] bg-[#0c0f14] px-2 text-xs text-slate-100 disabled:opacity-45"
              disabled={!canManage || triggerDailySyncMutation.isPending}
              onClick={() => triggerDailySyncMutation.mutate()}
              title={canManage ? "触发全市场日线同步任务" : "当前账号无管理权限"}
            >
              <Play className="size-3.5" />
              触发日线同步
            </button>
          </div>
        </div>
      </header>

      {pageError ? (
        <div className="border-b border-rose-500/30 bg-rose-500/10 px-3 py-2 text-xs text-rose-200">{pageError}</div>
      ) : null}

      <div className="grid min-h-0 flex-1 grid-cols-[minmax(0,1fr)_360px]">
        <section className="flex min-h-0 min-w-0 flex-col border-r border-[var(--border)] bg-[#0f1115]">
          <div className="terminal-scrollbar flex h-10 items-center gap-5 overflow-x-auto border-b border-[var(--border)] bg-[#12161c] px-3 text-[11px] text-[var(--muted)]">
            <span>服务器时间 {bootstrapQuery.data?.server_time || "--"}</span>
            <span>{runs.length} 条运行记录</span>
            <span>{schedules.length} 条调度配置</span>
            <span>{canManage ? "权限: 可管理" : "权限: 只读"}</span>
          </div>

          <div className="terminal-scrollbar min-h-0 flex-1 overflow-auto">
            <table className="w-full min-w-[860px] border-collapse text-xs">
              <thead className="sticky top-0 z-10 bg-[#151a21] text-[11px] text-slate-300">
                <tr>
                  <th className="border-b border-[var(--border)] px-2 py-2 text-left font-medium">运行ID</th>
                  <th className="border-b border-[var(--border)] px-2 py-2 text-left font-medium">任务类型</th>
                  <th className="border-b border-[var(--border)] px-2 py-2 text-left font-medium">状态</th>
                  <th className="border-b border-[var(--border)] px-2 py-2 text-left font-medium">范围</th>
                  <th className="border-b border-[var(--border)] px-2 py-2 text-left font-medium">开始时间</th>
                  <th className="border-b border-[var(--border)] px-2 py-2 text-left font-medium">摘要</th>
                  <th className="border-b border-[var(--border)] px-2 py-2 text-left font-medium">操作</th>
                </tr>
              </thead>
              <tbody>
                {runs.map((run) => {
                  const runId = toRunId(run.id);
                  const active = runId === selectedRunId;
                  const status = String(run.status || "").toUpperCase();
                  return (
                    <tr
                      key={runId}
                      className={`cursor-pointer border-b border-[var(--border)]/60 ${active ? "bg-sky-500/10" : "hover:bg-white/4"}`}
                      onClick={() => setSelectedRunId(runId)}
                    >
                      <td className="px-2 py-2 align-top font-medium text-slate-100">#{runId}</td>
                      <td className="px-2 py-2 align-top text-slate-300">{run.task_label || run.task_type || "--"}</td>
                      <td className={`px-2 py-2 align-top font-semibold ${statusTone(status)}`}>{status || "--"}</td>
                      <td className="px-2 py-2 align-top text-slate-400">{run.scope_type || "--"}</td>
                      <td className="px-2 py-2 align-top text-slate-400">{formatTimestamp(run.started_at || run.queued_at)}</td>
                      <td className="px-2 py-2 align-top text-slate-400">{run.summary_text || "--"}</td>
                      <td className="px-2 py-2 align-top">
                        <div className="flex items-center gap-1">
                          <button
                            className="rounded border border-[var(--border)] bg-[#0c0f14] px-1.5 py-0.5 text-[10px] text-slate-300 disabled:opacity-45"
                            disabled={!canManage || !["RUNNING", "QUEUED"].includes(status) || cancelRunMutation.isPending}
                            onClick={(event) => {
                              event.stopPropagation();
                              cancelRunMutation.mutate(runId);
                            }}
                          >
                            取消
                          </button>
                          <button
                            className="rounded border border-[var(--border)] bg-[#0c0f14] px-1.5 py-0.5 text-[10px] text-slate-300 disabled:opacity-45"
                            disabled={!canManage || status === "RUNNING" || retryRunMutation.isPending}
                            onClick={(event) => {
                              event.stopPropagation();
                              retryRunMutation.mutate(runId);
                            }}
                          >
                            重试
                          </button>
                        </div>
                      </td>
                    </tr>
                  );
                })}
                {!runs.length ? (
                  <tr>
                    <td className="px-3 py-6 text-center text-[11px] text-[var(--muted)]" colSpan={7}>
                      暂无运行记录
                    </td>
                  </tr>
                ) : null}
              </tbody>
            </table>
          </div>
        </section>

        <aside className="flex min-h-0 min-w-0 flex-col bg-[#0d1015]">
          <section className="border-b border-[var(--border)] px-3 py-2">
            <h2 className="text-xs font-semibold text-slate-200">调度配置</h2>
          </section>

          <section className="border-b border-[var(--border)] px-3 py-2">
            <form
              className="grid gap-2 text-[11px]"
              onSubmit={(event) => {
                event.preventDefault();
                saveScheduleMutation.mutate(scheduleForm);
              }}
            >
              <label className="grid gap-1">
                <span className="text-[var(--muted)]">计划名称</span>
                <input
                  className="h-7 rounded border border-[var(--border)] bg-[#0c0f14] px-2 text-slate-100"
                  value={scheduleForm.name}
                  onChange={(event) => setScheduleForm((prev) => ({ ...prev, name: event.target.value }))}
                  placeholder="例如：收盘后同步"
                />
              </label>
              <div className="grid grid-cols-3 gap-2">
                <label className="grid gap-1">
                  <span className="text-[var(--muted)]">任务</span>
                  <select
                    className="h-7 rounded border border-[var(--border)] bg-[#0c0f14] px-1.5 text-slate-100"
                    value={scheduleForm.taskType}
                    onChange={(event) => setScheduleForm((prev) => ({ ...prev, taskType: event.target.value }))}
                  >
                    {taskTypes.map((item) => (
                      <option key={item.key} value={item.key}>
                        {item.label || item.key}
                      </option>
                    ))}
                  </select>
                </label>
                <label className="grid gap-1">
                  <span className="text-[var(--muted)]">范围</span>
                  <select
                    className="h-7 rounded border border-[var(--border)] bg-[#0c0f14] px-1.5 text-slate-100"
                    value={scheduleForm.scopeType}
                    onChange={(event) => setScheduleForm((prev) => ({ ...prev, scopeType: event.target.value }))}
                  >
                    <option value="all">all</option>
                    <option value="universe">universe</option>
                    <option value="symbols">symbols</option>
                    <option value="trade_date">trade_date</option>
                  </select>
                </label>
                <label className="grid gap-1">
                  <span className="text-[var(--muted)]">频率</span>
                  <select
                    className="h-7 rounded border border-[var(--border)] bg-[#0c0f14] px-1.5 text-slate-100"
                    value={scheduleForm.scheduleKind}
                    onChange={(event) =>
                      setScheduleForm((prev) => ({
                        ...prev,
                        scheduleKind: (event.target.value as ScheduleFormState["scheduleKind"]) || "daily",
                      }))
                    }
                  >
                    {(scheduleKinds.length ? scheduleKinds : [{ key: "daily", label: "每日" }]).map((item) => (
                      <option key={item.key} value={item.key}>
                        {item.label || item.key}
                      </option>
                    ))}
                  </select>
                </label>
              </div>
              <div className="grid grid-cols-2 gap-2">
                <label className="grid gap-1">
                  <span className="text-[var(--muted)]">触发时间</span>
                  <input
                    className="h-7 rounded border border-[var(--border)] bg-[#0c0f14] px-2 text-slate-100"
                    type="time"
                    value={scheduleForm.time}
                    onChange={(event) => setScheduleForm((prev) => ({ ...prev, time: event.target.value }))}
                  />
                </label>
                {scheduleForm.scheduleKind === "weekly" ? (
                  <label className="grid gap-1">
                    <span className="text-[var(--muted)]">周几</span>
                    <input
                      className="h-7 rounded border border-[var(--border)] bg-[#0c0f14] px-2 text-slate-100"
                      value={scheduleForm.weekdays}
                      onChange={(event) => setScheduleForm((prev) => ({ ...prev, weekdays: event.target.value }))}
                      placeholder="1,2,3,4,5"
                    />
                  </label>
                ) : null}
                {scheduleForm.scheduleKind === "monthly" ? (
                  <label className="grid gap-1">
                    <span className="text-[var(--muted)]">每月</span>
                    <input
                      className="h-7 rounded border border-[var(--border)] bg-[#0c0f14] px-2 text-slate-100"
                      value={scheduleForm.monthdays}
                      onChange={(event) => setScheduleForm((prev) => ({ ...prev, monthdays: event.target.value }))}
                      placeholder="1,15,28"
                    />
                  </label>
                ) : null}
              </div>
              <div className="flex items-center gap-2">
                <button
                  className="rounded border border-[var(--border)] bg-[#0c0f14] px-2 py-1 text-[11px] text-slate-100 disabled:opacity-45"
                  disabled={!canManage || saveScheduleMutation.isPending}
                  type="submit"
                >
                  {scheduleForm.editingScheduleId ? "更新计划" : "创建计划"}
                </button>
                <button
                  className="rounded border border-[var(--border)] bg-[#0c0f14] px-2 py-1 text-[11px] text-[var(--muted)] disabled:opacity-45"
                  disabled={saveScheduleMutation.isPending}
                  onClick={() => resetScheduleForm()}
                  type="button"
                >
                  清空
                </button>
                <span className="truncate text-[10px] text-[var(--muted)]">
                  {scheduleForm.editingScheduleId ? `正在编辑 #${scheduleForm.editingScheduleId}` : "新建计划"}
                </span>
              </div>
            </form>
          </section>

          <section className="terminal-scrollbar max-h-[28%] min-h-[150px] overflow-auto border-b border-[var(--border)]">
            <table className="w-full border-collapse text-[11px]">
              <thead className="sticky top-0 z-10 bg-[#151a21] text-slate-300">
                <tr>
                  <th className="border-b border-[var(--border)] px-2 py-1.5 text-left font-medium">名称</th>
                  <th className="border-b border-[var(--border)] px-2 py-1.5 text-left font-medium">任务</th>
                  <th className="border-b border-[var(--border)] px-2 py-1.5 text-left font-medium">状态</th>
                  <th className="border-b border-[var(--border)] px-2 py-1.5 text-left font-medium">频率</th>
                  <th className="border-b border-[var(--border)] px-2 py-1.5 text-left font-medium">下次执行</th>
                  <th className="border-b border-[var(--border)] px-2 py-1.5 text-left font-medium">操作</th>
                </tr>
              </thead>
              <tbody>
                {schedules.map((schedule) => {
                  const scheduleId = Number(schedule.id || 0);
                  const scheduleStatus = String(schedule.status || "").toUpperCase();
                  return (
                    <tr key={String(schedule.id)} className="border-b border-[var(--border)]/60">
                      <td className="px-2 py-1.5 text-slate-200">{schedule.name || schedule.task_type || "--"}</td>
                      <td className="px-2 py-1.5 text-slate-400">{schedule.task_type || "--"}</td>
                      <td className={`px-2 py-1.5 font-semibold ${statusTone(schedule.status)}`}>{schedule.status || "--"}</td>
                      <td className="px-2 py-1.5 text-slate-400">{scheduleFrequencyLabel(schedule)}</td>
                      <td className="px-2 py-1.5 text-slate-400">{formatTimestamp(schedule.next_run_time || schedule.updated_at)}</td>
                      <td className="px-2 py-1.5">
                        <div className="flex items-center gap-1">
                          <button
                            className="rounded border border-[var(--border)] bg-[#0c0f14] px-1.5 py-0.5 text-[10px] text-slate-300 disabled:opacity-45"
                            disabled={!canManage}
                            onClick={() => loadScheduleToForm(schedule)}
                          >
                            编辑
                          </button>
                          <button
                            className="rounded border border-[var(--border)] bg-[#0c0f14] px-1.5 py-0.5 text-[10px] text-slate-300 disabled:opacity-45"
                            disabled={!canManage || toggleScheduleMutation.isPending}
                            onClick={() =>
                              toggleScheduleMutation.mutate({
                                id: scheduleId,
                                action: scheduleStatus === "PAUSED" ? "resume" : "pause",
                              })
                            }
                          >
                            {scheduleStatus === "PAUSED" ? "恢复" : "暂停"}
                          </button>
                          <button
                            className="rounded border border-[var(--border)] bg-[#0c0f14] px-1.5 py-0.5 text-[10px] text-rose-200 disabled:opacity-45"
                            disabled={!canManage || deleteScheduleMutation.isPending}
                            onClick={() => {
                              if (window.confirm(`确认删除计划 #${scheduleId}？`)) {
                                deleteScheduleMutation.mutate(scheduleId);
                              }
                            }}
                          >
                            删除
                          </button>
                        </div>
                      </td>
                    </tr>
                  );
                })}
                {!schedules.length ? (
                  <tr>
                    <td className="px-2 py-4 text-center text-[11px] text-[var(--muted)]" colSpan={6}>
                      暂无调度任务
                    </td>
                  </tr>
                ) : null}
              </tbody>
            </table>
          </section>

          <section className="border-b border-[var(--border)] px-3 py-2">
            <h2 className="text-xs font-semibold text-slate-200">运行步骤 {selectedRun ? `#${toRunId(selectedRun.id)}` : ""}</h2>
          </section>
          <section className="terminal-scrollbar max-h-[26%] min-h-[120px] overflow-auto border-b border-[var(--border)] px-3 py-2">
            {(runStepsQuery.data || []).map((step, idx) => (
              <div key={`${String(step.id || idx)}:${step.step_key || ""}`} className="border-b border-[var(--border)]/50 py-1.5 text-[11px]">
                <div className="flex items-center gap-2">
                  <span className="text-slate-200">{step.step_label || step.step_key || `step_${idx + 1}`}</span>
                  <span className={`font-semibold ${statusTone(step.status)}`}>{step.status || "--"}</span>
                </div>
                <div className="mt-0.5 text-[10px] text-slate-400">
                  {formatTimestamp(step.started_at)} → {formatTimestamp(step.finished_at)}
                </div>
                <div className="mt-0.5 break-words text-[10px] text-slate-400">{step.summary_text || step.error_msg || "--"}</div>
              </div>
            ))}
            {!(runStepsQuery.data || []).length ? (
              <div className="py-4 text-center text-[11px] text-[var(--muted)]">暂无步骤信息</div>
            ) : null}
          </section>

          <section className="border-b border-[var(--border)] px-3 py-2">
            <h2 className="text-xs font-semibold text-slate-200">运行日志 {selectedRun ? `#${toRunId(selectedRun.id)}` : ""}</h2>
          </section>
          <section className="terminal-scrollbar min-h-0 flex-1 overflow-auto px-3 py-2">
            {(logsQuery.data?.items || []).map((item, idx) => (
              <div key={`${String(item.id || idx)}:${item.ts || ""}`} className="border-b border-[var(--border)]/50 py-1.5 text-[11px]">
                <div className="flex items-center gap-2 text-slate-400">
                  <span>{formatTimestamp(item.ts)}</span>
                  <span className={`font-semibold ${statusTone(item.level)}`}>{item.level || "INFO"}</span>
                </div>
                <div className="mt-1 whitespace-pre-wrap break-words text-slate-200">{item.message || "--"}</div>
              </div>
            ))}
            {!(logsQuery.data?.items || []).length ? (
              <div className="py-6 text-center text-[11px] text-[var(--muted)]">暂无日志</div>
            ) : null}
          </section>
        </aside>
      </div>
    </main>
  );
}
